/**
 * runidea.ai — Phase 13 — Step-0 "guided picker" with 4 large cards.
 *
 * Replaces the small segmented selector for the wizard's first step.
 * Shows: Rental / Flip / BRRRR / Multifamily as full-bleed cards with
 * icon + headline + "best for" bullets. Continue is disabled until the
 * user actively confirms a selection (localStorage may pre-highlight the
 * last pick but does NOT auto-confirm — explicit user consent per spec).
 *
 * Parent owns canonical `value`. This component emits:
 *   onSelect(id)     — when a card is clicked (sets pending selection)
 *   onConfirm(id)    — when Continue is clicked (advances the wizard)
 */
import { Building2, Hammer, Home, Wrench, User, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFeatureFlags } from "@/hooks/useFeatureFlag";

export const DEAL_TYPE_IDS = ["rental", "flip", "brrrr", "multifamily", "personal_residence"];
export const DEAL_TYPE_DEFAULT = "rental";
export const LAST_DEAL_TYPE_KEY = "runidea.deal-wizard.last-deal-type";

const CARDS = [
  {
    id: "rental",
    label: "Rental",
    tagline: "Buy & hold underwriting",
    Icon: Home,
    accent: "emerald",
    description: "Long-term passive income from a tenant-occupied property.",
    bullets: ["Single-family or 1-unit condo", "Predictable monthly cash flow", "Cap rate, CoC, DSCR scoring"],
  },
  {
    id: "flip",
    label: "Flip",
    tagline: "Buy → renovate → resell",
    Icon: Hammer,
    accent: "orange",
    description: "Short-term project. Sell after rehab — no rental income needed.",
    bullets: ["3–12 month hold", "ARV-margin & 70%-rule scoring", "Annualised ROI on cash invested"],
  },
  {
    id: "brrrr",
    label: "BRRRR",
    tagline: "Buy, Rehab, Rent, Refi, Repeat",
    Icon: Wrench,
    accent: "purple",
    description: "Recycle capital — refi out at ARV and use the rental for cash flow.",
    bullets: ["Two-stage financing (initial + refi)", "Capital-recycled %", "Detects infinite-return deals"],
  },
  {
    id: "multifamily",
    label: "Multifamily",
    tagline: "2–4 unit small MF",
    Icon: Building2,
    accent: "blue",
    description: "Editable unit-mix grid rolls up into a blended rental analysis.",
    bullets: ["Per-unit rent & vacancy", "Total potential vs effective rent", "Rental scoring applied to mix"],
  },
  {
    // Phase 48 — 4th deal type. Owner-occupant underwriting with a
    // three-path wealth comparison (Live / Rent / Flip). Different
    // question, different KPIs, different Tri-View.
    id: "personal_residence",
    label: "Personal Residence",
    tagline: "Live in it — smart move?",
    Icon: User,
    accent: "teal",
    description: "Analyze a home you'll actually live in. Compare Live vs Rent vs Flip.",
    bullets: [
      "Owner-occupant loan rates + tax deductions",
      "Break-even years vs renting",
      "FL homestead + Section 121 modeling",
    ],
  },
];

const ACCENT_MAP = {
  emerald: { ring: "ring-emerald-500", border: "border-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700", iconBg: "bg-emerald-100" },
  orange: { ring: "ring-orange-500", border: "border-orange-500", bg: "bg-orange-50", text: "text-orange-700", iconBg: "bg-orange-100" },
  purple: { ring: "ring-purple-500", border: "border-purple-500", bg: "bg-purple-50", text: "text-purple-700", iconBg: "bg-purple-100" },
  blue: { ring: "ring-blue-500", border: "border-blue-500", bg: "bg-blue-50", text: "text-blue-700", iconBg: "bg-blue-100" },
  teal: { ring: "ring-teal-500", border: "border-teal-500", bg: "bg-teal-50", text: "text-teal-700", iconBg: "bg-teal-100" },
};

export default function DealTypePicker({ value, onSelect, onConfirm, suggestedFromStorage }) {
  const flags = useFeatureFlags();
  // Filter cards by feature flags. Defaults to enabled when flag is missing.
  const visibleCards = CARDS.filter((c) => {
    if (c.id === "multifamily" && flags.multifamily_enabled === false) return false;
    if (c.id === "brrrr" && flags.brrrr_enabled === false) return false;
    if (c.id === "flip" && flags.flip_enabled === false) return false;
    return true;
  });
  return (
    <div className="space-y-6" data-testid="deal-type-picker">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">
          What kind of deal are you analyzing?
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Each path runs a different underwriting model — pick the one that matches
          your strategy and we&apos;ll tailor the inputs, math, and scoring.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2" data-testid="deal-type-cards">
        {visibleCards.map((c) => {
          const a = ACCENT_MAP[c.accent];
          const selected = value === c.id;
          const wasLast = suggestedFromStorage === c.id && !value;
          const Icon = c.Icon;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(c.id)}
              data-testid={`deal-type-card-${c.id}`}
              data-selected={selected ? "true" : "false"}
              className={[
                "relative flex flex-col gap-3 rounded-2xl border-2 p-5 text-left transition-all duration-150",
                "hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                selected
                  ? `${a.border} ${a.bg} ring-2 ${a.ring} ring-offset-2 shadow-md`
                  : wasLast
                  ? "border-slate-300 bg-white shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300",
              ].join(" ")}
            >
              {selected && (
                <span
                  className={`absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold ${a.text}`}
                  aria-hidden="true"
                >
                  <Check className="h-3.5 w-3.5" />
                </span>
              )}
              {wasLast && !selected && (
                <span
                  data-testid={`deal-type-${c.id}-last-used`}
                  className="absolute right-3 top-3 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                >
                  Last used
                </span>
              )}
              <div className="flex items-start gap-3">
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${a.iconBg} ${a.text}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-base font-bold leading-tight text-slate-900">{c.label}</p>
                  <p className="mt-0.5 text-xs font-medium text-slate-500">{c.tagline}</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-slate-600">{c.description}</p>
              <ul className="mt-1 space-y-1 text-xs text-slate-500">
                {c.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-1.5">
                    <span className={`mt-1 inline-block h-1 w-1 flex-shrink-0 rounded-full ${a.text}`}></span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 pt-5">
        <p className="text-xs text-slate-500">
          {value ? (
            <>
              You picked <span className="font-semibold text-slate-700">{CARDS.find((c) => c.id === value)?.label}</span>.
              Click Continue to start entering details.
            </>
          ) : (
            <>Select a deal type to continue.</>
          )}
        </p>
        <Button
          type="button"
          disabled={!value}
          onClick={() => value && onConfirm(value)}
          data-testid="deal-type-continue"
          className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-40 disabled:hover:translate-y-0"
        >
          Continue <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
